import { useState, useEffect } from "react";
import { View, StyleSheet, useWindowDimensions } from "react-native";
import { router, useLocalSearchParams, Redirect } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image, ImageSource } from "expo-image";
import useAuth from "@/hooks/useAuth";

export default function ClockInIdentityScreen() {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const { shift } = useLocalSearchParams<{ shift: string }>();
  const { session, loading } = useAuth();
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const [email, setEmail] = useState("");

  const showLogo = width <= 900;

  // If user is already authenticated, skip to confirm
  if (session && !loading) {
    return <Redirect href={`/clock-in/${shift}/confirm`} />;
  }

  const handleContinue = () => {
    router.push(`/clock-in/${shift}/confirm?email=${encodeURIComponent(email)}`);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        {/* Logo Section */}
        {showLogo && (
          <View style={styles.logoSection}>
            <Image source={[logo]} style={styles.logo} />
            <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
            <ThemedText style={[styles.logoSubtitle, { color: colors.textSecondary }]}>Shift Clock-In</ThemedText>
          </View>
        )}

        {/* Clock In Card */}
        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Clock In</ThemedText>
          <ThemedText style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
            Enter your email address to clock in to this shift
          </ThemedText>

          {/* Form */}
          <View style={styles.form}>
            <Input
              id="email-input"
              icon="email"
              placeholder="Email Address"
              value={email}
              onChangeText={setEmail}
              autoComplete="email"
              containerStyle={styles.input}
              keyboardType="email-address"
              autoCapitalize="none"
              autoFocus
            />

            <Button
              id="continue-btn"
              text="Continue"
              onPress={handleContinue}
              icon="arrow-right"
              iconPosition="right"
              iconSize={20}
              iconColor="white"
              enabled={!!email && email.includes("@")}
              style={[
                styles.continueButton,
                {
                  backgroundColor: email && email.includes("@") ? colors.primary : colors.border,
                  opacity: email && email.includes("@") ? 1 : 0.6,
                },
              ]}
              textStyle={styles.continueButtonText}
            />
          </View>
        </View>

        {/* Help Card */}
        <View style={[styles.helpCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialCommunityIcons name="help-circle-outline" size={20} color={colors.textSecondary} />
          <ThemedText style={[styles.helpText, { color: colors.textSecondary }]}>
            Need help? Ask a supervisor for assistance.
          </ThemedText>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 56,
    width: 56,
    marginBottom: 12,
  },
  logoTitle: {
    fontSize: 24,
    fontWeight: "800",
    letterSpacing: 2,
  },
  logoSubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  card: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  form: {
    gap: 16,
  },
  input: {
    minWidth: "100%",
  },
  continueButton: {
    borderRadius: 10,
    minHeight: 50,
    marginTop: 8,
  },
  continueButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  helpCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  helpText: {
    fontSize: 14,
    flex: 1,
  },
});
